import { captureError, markReloaded, reportCapturedError } from "@/lib/errorReporting";
import { Button, Result } from "antd";
import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from "react";
import { useRouteError } from "react-router";

type Phase = "initial" | "persisted" | "reported";

/**
 * Friendly full-page fallback for unexpected application errors. The error is recorded
 * automatically; after one reload that does not fix it, a "Report error" button appears.
 */
export const ErrorFallback = ({ error }: { error: unknown }) => {
  const [phase, setPhase] = useState<Phase>("initial");
  const [logId, setLogId] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const [reportFailed, setReportFailed] = useState(false);

  useEffect(() => {
    console.error(error);
    let active = true;
    void captureError(error).then(({ id, reloadedBefore }) => {
      if (!active) return;
      setLogId(id);
      if (reloadedBefore) setPhase("persisted");
    });
    return () => {
      active = false;
    };
  }, [error]);

  const handleReload = () => {
    markReloaded();
    window.location.reload();
  };

  const handleReport = async () => {
    setReporting(true);
    setReportFailed(false);
    const ok = await reportCapturedError(error, logId);
    setReporting(false);
    if (ok) setPhase("reported");
    else setReportFailed(true);
  };

  if (phase === "reported") {
    return (
      <div className="flex min-h-screen items-center justify-center px-4" role="status">
        <Result
          status="success"
          title="Error reported"
          subTitle="Thanks! Your error has been reported and our team will look into it."
          extra={
            <Button type="primary" onClick={() => (window.location.href = "/")}>
              Back to home
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4" role="alert">
      <Result
        status="warning"
        title="Something went wrong"
        subTitle={
          phase === "persisted"
            ? "Reloading didn't fix it. You can report this error so our team can fix it."
            : "An unexpected error occurred. Reloading the page usually fixes it."
        }
        extra={[
          <Button key="reload" type={phase === "persisted" ? "default" : "primary"} onClick={handleReload}>
            Reload
          </Button>,
          phase === "persisted" ? (
            <Button key="report" type="primary" loading={reporting} onClick={handleReport}>
              Report error
            </Button>
          ) : null,
        ]}
      >
        {reportFailed ? <p className="text-center text-red-500">Couldn't send the report. Please try again.</p> : null}
      </Result>
    </div>
  );
};

/** React Router `errorElement` content that replaces the default developer error page. */
export const RouteErrorPage = () => <ErrorFallback error={useRouteError()} />;

/** Route-level error element; undefined in development so React Router's detailed error page stays available. */
export const routeErrorElement = import.meta.env.PROD ? <RouteErrorPage /> : undefined;

interface BoundaryState {
  error: unknown;
  failed: boolean;
}

/** Catches render errors that happen outside the router, such as in app providers. */
export class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { error: null, failed: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (!import.meta.env.PROD) throw this.state.error;
    return <ErrorFallback error={this.state.error} />;
  }
}
